import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import {
  CheckIcon,
  RefreshIcon,
  SaveIcon,
} from '../components/icons';

/**
 * Theme detail + editor.
 *
 * Reads /v1/admin/themes/<theme_id>, groups the CSS custom
 * properties by concern (colours, radii, shadows, type), and
 * offers a Save that PUTs the whole variant back.
 *
 * Live preview is a card styled with the current in-memory vars
 * rendered below the inputs — no iframe, no SDK reinit, just CSS.
 *
 * Authoring of the full overlay still lives in the SDK admin
 * overlay; this is the dashboard-side equivalent for "change the
 * palette without opening a page that embeds the SDK".
 */
export function ThemeDetail() {
  const { theme_id: themeId } = useParams();
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const api = useMemo(
    () => createApiClient(getToken, { projectId: project?.project_id || null }),
    [getToken, project?.project_id],
  );

  const [theme, setTheme] = useState(null);
  const [vars, setVars] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const t = await api(`/v1/admin/themes/${encodeURIComponent(themeId)}`);
      setTheme(t);
      setVars({ ...(t?.variables || t?.light?.variables || {}) });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, themeId]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api(`/v1/admin/themes/${encodeURIComponent(themeId)}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: theme.name,
          variables: vars,
        }),
      });
      setNotice('Saved. SDK picks up the change on next config refresh (~30s).');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function setAsDefault() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api(`/v1/admin/themes/${encodeURIComponent(themeId)}/set-default`, {
        method: 'POST',
      });
      setNotice('Set as default for this tenant.');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  function resetToSaved() {
    setVars({ ...(theme?.variables || theme?.light?.variables || {}) });
    setNotice(null);
  }

  const groups = useMemo(() => groupVariables(vars), [vars]);
  const { slug } = useParams();

  if (loading) {
    return <div className="state"><div className="state__title">Loading theme…</div></div>;
  }
  if (error || !theme) {
    return (
      <div className="state state--error">
        <div className="state__title">Couldn't load theme</div>
        <div>{error || `No theme with id ${themeId}`}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="../themes" className="btn btn--ghost" style={{ padding: 0 }}>← All themes</Link>
      </div>

      <header className="card theme-detail__header">
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.02em', fontFamily: 'var(--font-mono)' }}>
            <code>{theme.theme_id}</code>
          </div>
          <h2 style={{ margin: '4px 0 6px' }}>
            {theme.name}
            {theme.is_default && (
              <span className="badge badge--default" style={{ marginLeft: 10, verticalAlign: 'middle' }}>
                Default
              </span>
            )}
          </h2>
        </div>
        <div className="row">
          {!theme.is_default && (
            <button
              type="button"
              className="btn btn--secondary"
              disabled={saving}
              onClick={setAsDefault}
            >
              <CheckIcon /> Set as default
            </button>
          )}
          <button type="button" className="btn btn--ghost" disabled={saving} onClick={resetToSaved}>
            <RefreshIcon /> Reset
          </button>
          <button type="button" className="btn btn--primary" disabled={saving} onClick={save}>
            <SaveIcon /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {notice && <div className="state" style={{ marginTop: 10 }}><div>{notice}</div></div>}

      <div className="theme-detail__grid">
        <div className="theme-detail__inputs">
          {groups.map((group) => (
            <section key={group.label} className="card">
              <h3 className="card__title">{group.label}</h3>
              <div className="theme-detail__vars">
                {group.vars.map(([key, value]) => (
                  <VarRow
                    key={key}
                    name={key}
                    value={value}
                    onChange={(next) => setVars((v) => ({ ...v, [key]: next }))}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="theme-detail__preview-wrap">
          <div className="card theme-detail__preview" style={previewStyle(vars)}>
            <div className="preview-title">Live preview</div>
            <p className="preview-body">
              Any change you make above is visible here instantly — no
              reload, no SDK reinit. Save when the palette clicks.
            </p>
            <button type="button" className="preview-cta">Primary CTA</button>
            <div className="preview-tooltip">
              <strong>Tooltip title</strong>
              <div>Spotlight uses these same variables for tooltips, modals, banners, and hotspots.</div>
            </div>
          </div>
        </div>
      </div>
      <style>{THEME_CSS}</style>
    </div>
  );
}

function VarRow({ name, value, onChange }) {
  const isColour = /^#[0-9a-f]{3,8}$/i.test((value || '').trim());
  return (
    <label className="theme-var">
      <span className="theme-var__name"><code>{name}</code></span>
      <span className="theme-var__controls">
        {isColour && (
          <input
            type="color"
            value={normaliseHex(value)}
            onChange={(e) => onChange(e.target.value)}
            aria-label={`${name} colour picker`}
          />
        )}
        <input
          className="input theme-var__text"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </span>
    </label>
  );
}

function normaliseHex(v) {
  const t = (v || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(t)) return t;
  if (/^#[0-9a-f]{3}$/i.test(t)) {
    return '#' + t.slice(1).split('').map((c) => c + c).join('');
  }
  return '#000000';
}

function groupVariables(vars) {
  const entries = Object.entries(vars || {});
  const bucket = {
    Colours: [],
    Radius: [],
    Shadows: [],
    Typography: [],
    Spacing: [],
    Other: [],
  };
  for (const [k, v] of entries) {
    if (k.includes('font') || k.includes('text-size') || k.includes('line-height')) {
      bucket.Typography.push([k, v]);
    } else if (k.includes('radius')) {
      bucket.Radius.push([k, v]);
    } else if (k.includes('shadow')) {
      bucket.Shadows.push([k, v]);
    } else if (k.includes('spacing') || k.includes('padding') || k.includes('margin')) {
      bucket.Spacing.push([k, v]);
    } else if (k.startsWith('--spot-') && /#|rgb|hsl/.test(String(v))) {
      bucket.Colours.push([k, v]);
    } else if (k.startsWith('--spot-')) {
      bucket.Other.push([k, v]);
    } else {
      bucket.Other.push([k, v]);
    }
  }
  return Object.entries(bucket)
    .filter(([, arr]) => arr.length > 0)
    .map(([label, arr]) => ({ label, vars: arr.sort(([a], [b]) => a.localeCompare(b)) }));
}

function previewStyle(vars) {
  const out = {};
  for (const [k, v] of Object.entries(vars || {})) {
    if (k.startsWith('--')) out[k] = v;
  }
  return out;
}

const THEME_CSS = `
  .theme-detail__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  }
  .theme-detail__grid {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);
    gap: 14px;
    margin-top: 14px;
    align-items: flex-start;
  }
  .theme-detail__inputs {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .theme-detail__preview-wrap {
    position: sticky;
    top: 24px;
  }
  .theme-detail__vars {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
  }
  .theme-var {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 6px 10px;
    border-radius: var(--r-md);
    background: var(--surface-1);
  }
  .theme-var__name {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .theme-var__controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .theme-var__controls input[type="color"] {
    width: 28px;
    height: 28px;
    border: 1px solid var(--border-1);
    border-radius: var(--r-sm);
    padding: 0;
    background: transparent;
    cursor: pointer;
  }
  .theme-var__text {
    width: 160px;
    font-family: var(--font-mono);
    font-size: 12px;
    padding: 6px 8px;
  }

  /* Preview is styled entirely from the theme's variables so
     edits appear instantly with no React re-theming. */
  .theme-detail__preview {
    background: var(--spot-bg, #ffffff);
    color: var(--spot-text, #111827);
    border-color: var(--spot-border, var(--border-1));
    box-shadow: var(--spot-shadow, var(--shadow-2));
    border-radius: var(--spot-radius-lg, var(--r-lg));
    padding: 24px;
    font-family: var(--spot-font-family, var(--font-sans));
  }
  .preview-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--spot-text-secondary, var(--text-3));
    margin-bottom: 10px;
  }
  .preview-body {
    margin: 0 0 14px;
    color: var(--spot-text, inherit);
    font-size: var(--spot-font-size-sm, 13px);
  }
  .preview-cta {
    background: var(--spot-primary, #4f46e5);
    color: var(--spot-primary-text, #ffffff);
    border: 0;
    border-radius: var(--spot-radius, 8px);
    padding: 8px 14px;
    font-weight: 600;
    cursor: pointer;
  }
  .preview-cta:hover {
    background: var(--spot-primary-hover, #4338ca);
  }
  .preview-tooltip {
    margin-top: 16px;
    padding: 12px 14px;
    background: var(--spot-bg-secondary, #f9fafb);
    border: 1px solid var(--spot-border, #e5e7eb);
    border-radius: var(--spot-radius, 8px);
    font-size: 13px;
  }
  .preview-tooltip strong { display: block; margin-bottom: 4px; }
`;
